import { toSVG } from './geo'
import { edgeCountryOf } from './segments'
import type { WorldFlowHop } from '@/analysis/worldFlowTypes'
import { resolveCatalogStudio } from '@/data/catalog'

export type PosMap = Record<string, { x: number; y: number }>

export function hopKey(type: string, value: string): string {
  return `${type}:${value}`
}

function offsetFor(index: number, total: number, radius = 14): { dx: number; dy: number } {
  if (total <= 1) return { dx: 0, dy: 0 }
  const angle = (index / total) * Math.PI * 2 - Math.PI / 2
  return { dx: Math.cos(angle) * radius, dy: Math.sin(angle) * radius }
}

/** Studio map position from hop geo or catalog match — never invent a demo site. */
function studioMapXY(h: WorldFlowHop): { x: number; y: number } | null {
  if (h.lat != null && h.lon != null) return toSVG(h.lat, h.lon)
  const cat = resolveCatalogStudio(h.value) ?? resolveCatalogStudio(h.label)
  if (cat && Number.isFinite(cat.lat) && Number.isFinite(cat.lon)) return toSVG(cat.lat, cat.lon)
  return null
}

export function clientCountry(h: WorldFlowHop): string | null {
  const raw = (h.country ?? (h.value.includes('|') ? h.value.split('|')[1] : h.value))?.trim()
  if (!raw) return null
  const cc = raw.toUpperCase()
  return /^[A-Z]{2}$/.test(cc) ? cc : null
}

/** ISO2 for an edge: catalog/API country, else label suffix. */
export function edgePlacementCountry(h: WorldFlowHop): string | null {
  if (h.country) {
    const cc = h.country.trim().toUpperCase()
    if (/^[A-Z]{2}$/.test(cc)) return cc
  }
  return edgeCountryOf(h.value)
}

/**
 * Map-space positions for hops that have real geo (hop lat/lon, catalog studio,
 * or country/continent centroid). Unplaced hops are omitted — never hashed onto
 * a random demo site.
 */
export function buildHopPositions(
  hops: WorldFlowHop[],
  countryCentroids: Record<string, { x: number; y: number }>,
  continentCentroids: Record<string, { x: number; y: number }> = {},
): PosMap {
  const out: PosMap = {}

  const edgesByBucket = new Map<string, WorldFlowHop[]>()
  for (const h of hops) {
    if (h.type !== 'cdn_edge') continue
    const bucket = (h.lat != null && h.lon != null)
      ? `geo:${h.lat.toFixed(2)},${h.lon.toFixed(2)}`
      : (edgePlacementCountry(h) ?? null)
    if (!bucket) continue
    const list = edgesByBucket.get(bucket) ?? []
    list.push(h)
    edgesByBucket.set(bucket, list)
  }

  const citiesByCc = new Map<string, WorldFlowHop[]>()
  for (const h of hops) {
    if (h.type !== 'city') continue
    if (h.lat != null && h.lon != null) continue
    const cc = clientCountry(h)
    if (!cc) continue
    const list = citiesByCc.get(cc) ?? []
    list.push(h)
    citiesByCc.set(cc, list)
  }

  const clientsByContinent = new Map<string, WorldFlowHop[]>()
  for (const h of hops) {
    if (h.type !== 'city') continue
    if (h.lat != null && h.lon != null) continue
    if (clientCountry(h)) continue
    const cont = (h.continent ?? h.value)?.trim()
    if (!cont) continue
    const list = clientsByContinent.get(cont) ?? []
    list.push(h)
    clientsByContinent.set(cont, list)
  }

  for (const h of hops) {
    const key = hopKey(h.type, h.value)

    if (h.type === 'studio') {
      // Prefer country centroid (same as client country parking) for realistic map placement.
      const cat = resolveCatalogStudio(h.value) ?? resolveCatalogStudio(h.label)
      const cc = String(h.country ?? cat?.country ?? '')
        .trim()
        .toUpperCase()
      if (cc && /^[A-Z]{2}$/.test(cc) && countryCentroids[cc]) {
        out[key] = countryCentroids[cc]!
        continue
      }
      const xy = studioMapXY(h)
      if (xy) out[key] = xy
      continue
    }

    if (h.type === 'cdn_edge') {
      if (h.lat != null && h.lon != null) {
        const bucket = `geo:${h.lat.toFixed(2)},${h.lon.toFixed(2)}`
        const list = edgesByBucket.get(bucket) ?? [h]
        const idx = Math.max(0, list.findIndex((e) => e.value === h.value))
        const { dx, dy } = offsetFor(idx, list.length, 10)
        const base = toSVG(h.lat, h.lon)
        out[key] = { x: base.x + dx, y: base.y + dy }
        continue
      }
      const cc = edgePlacementCountry(h)
      if (cc && countryCentroids[cc]) {
        const list = edgesByBucket.get(cc) ?? [h]
        const idx = Math.max(0, list.findIndex((e) => e.value === h.value))
        const { dx, dy } = offsetFor(idx, list.length)
        const c = countryCentroids[cc]!
        out[key] = { x: c.x + dx, y: c.y + dy }
      }
      // No Atlantic parking spot for unknown edges — omit until geo exists.
      continue
    }

    if (h.type === 'city') {
      if (h.lat != null && h.lon != null) {
        out[key] = toSVG(h.lat, h.lon)
      } else {
        const cc = clientCountry(h)
        if (cc && countryCentroids[cc]) {
          const list = citiesByCc.get(cc) ?? [h]
          const idx = Math.max(0, list.findIndex((c) => c.value === h.value))
          const { dx, dy } = offsetFor(idx, list.length, 12)
          const c = countryCentroids[cc]!
          out[key] = { x: c.x + dx, y: c.y + dy }
        } else {
          const cont = (h.continent ?? h.value)?.trim()
          if (cont && continentCentroids[cont]) {
            const list = clientsByContinent.get(cont) ?? [h]
            const idx = Math.max(0, list.findIndex((c) => c.value === h.value))
            const { dx, dy } = offsetFor(idx, list.length, 18)
            const c = continentCentroids[cont]!
            out[key] = { x: c.x + dx, y: c.y + dy }
          }
          // No demo-site hash for orphan cities.
        }
      }
    }
  }

  return out
}

/** Country codes that must be centroid-resolved for placement fallbacks + region stacks. */
export function countriesNeededForPlacement(hops: WorldFlowHop[]): string[] {
  const ccs = new Set<string>()
  for (const h of hops) {
    if (h.type === 'studio') {
      const cat = resolveCatalogStudio(h.value) ?? resolveCatalogStudio(h.label)
      const raw = h.country ?? cat?.country
      if (raw) {
        const cc = String(raw).trim().toUpperCase()
        if (/^[A-Z]{2}$/.test(cc)) ccs.add(cc)
      }
    }
    if (h.type === 'cdn_edge') {
      const cc = edgePlacementCountry(h)
      if (cc) ccs.add(cc)
    }
    if (h.type === 'city') {
      const cc = clientCountry(h)
      if (cc) ccs.add(cc)
    }
  }
  return [...ccs]
}

/** Continent labels that need SVG centroid placement (continent grain). */
export function continentsNeededForPlacement(hops: WorldFlowHop[]): string[] {
  const out = new Set<string>()
  for (const h of hops) {
    if (h.type !== 'city') continue
    if (clientCountry(h)) continue
    const cont = (h.continent ?? h.value)?.trim()
    if (cont) out.add(cont)
  }
  return [...out]
}
